import React, { ReactNode, useReducer } from 'react';
import { DurationInput } from 'luxon';

import { DateTime, getDateTime } from 'infras/datetime';
import { AccountCalendarListItem } from 'presentations/api/schema/account';
import { PendingEventType } from 'domains/models/pending_event/pendingEventType';
import { getWeekStartedAt } from 'presentations/views/helpers/calendar';

export interface State {
  initialized: boolean;
  isUsingCache: boolean;
  calendarUnit: CalendarUnitType;
  currentDate: Date;
  startedAt: Date;
  endedAt: Date;
  accountCalendars: AccountCalendarListItem[];
  selectedUserUids: string[];
  formType: FormType;
  dates: EventDate[];
  duration: number;
  intervalMinutes: number;
  timeZone: string;
  pendingEventType: PendingEventType | null;
  isDragging: boolean;
}

export interface EventDate {
  id: string;
  start?: Date;
  end?: Date;
  isAllday: boolean;
  userUid?: string;
}

export type CalendarUnitType = 'month' | 'week' | 'day' | '3day';

export enum FormType {
  Event = 'event',
  Schedule = 'schedule',
  Poll = 'poll',
}

export const DATES_MAX_COUNT = 30;
export const POLL_DATES_MAX_COUNT = 40;
export const CREATING_EVENT_ID_PREFIX = 'creating-';
export const CREATING_EVENT_CLASS_NAME = 'creating-event';

const initialState: (
  locale?: string,
  timeZone?: string,
  formType?: FormType,
  startWeekDay?: number,
) => State = (locale, timeZone, formType, startWeekDay) => {
  const today = getDateTime(null, timeZone);
  const newStartedAt = getWeekStartedAt(today, startWeekDay ?? 0);

  return {
    initialized: false,
    isUsingCache: true,
    viewType: 'calendar',
    calendarUnit: 'week',
    currentDate: today.startOf('day').toJSDate(),
    startedAt: newStartedAt.toJSDate(),
    endedAt: newStartedAt.plus({ days: 6 }).endOf('day').toJSDate(),
    accountCalendars: [],
    selectedUserUids: [],
    formType: formType ?? FormType.Event,
    dates: [],
    duration: 30,
    intervalMinutes: 30,
    timeZone: timeZone || 'Asia/Tokyo',
    pendingEventType: PendingEventType.CustomEvent,
    isDragging: false,
  };
};

export const setCalendarUnit = (
  calendarUnit: CalendarUnitType,
  startWeekDay: number,
) =>
  ({
    type: 'SET_CALENDAR_UNIT',
    calendarUnit,
    startWeekDay,
  } as const);
export const setCurrentDate = (currentDate: Date) =>
  ({
    type: 'SET_CURRENT_DATE',
    currentDate,
  } as const);
export const setStartedAt = (startedAt: Date) =>
  ({
    type: 'SET_STARTED_AT',
    startedAt,
  } as const);
export const initialize = (
  isUsingCache: boolean,
  accountCalendars: AccountCalendarListItem[],
) =>
  ({
    type: 'INITIALIZE',
    isUsingCache,
    accountCalendars,
  } as const);
export const setSelectedUserUids = (selectedUserUids: string[]) =>
  ({
    type: 'SET_SELECTED_USER_UIDS',
    selectedUserUids,
  } as const);
export const initializeForm = (timeZone: string) =>
  ({
    type: 'INITIALIZE_FORM',
    timeZone,
  } as const);
export const setFormType = (formType: FormType) =>
  ({
    type: 'SET_FORM_TYPE',
    formType,
  } as const);
export const setDate = (date: EventDate, index?: number) =>
  ({
    type: 'SET_DATE',
    date,
    index,
  } as const);
export const removeDate = (id: string) =>
  ({
    type: 'REMOVE_DATE',
    id,
  } as const);
export const clearDate = () =>
  ({
    type: 'CLEAR_DATE',
  } as const);
export const setDuration = (duration: number) =>
  ({
    type: 'SET_DURATION',
    duration,
  } as const);
export const setIntervalMinutes = (intervalMinutes: number) =>
  ({
    type: 'SET_INTERVAL_MINUTES',
    intervalMinutes,
  } as const);
export const setTimeZone = (timeZone: string) =>
  ({
    type: 'SET_TIMEZONE',
    timeZone,
  } as const);
export const setPendingEventType = (pendingEventType: PendingEventType) =>
  ({
    type: 'SET_PENDING_EVENT_TYPE',
    pendingEventType,
  } as const);
export const setIsDragging = (isDragging: boolean) =>
  ({
    type: 'SET_IS_DRAGGING',
    isDragging,
  } as const);

type Action =
  | ReturnType<typeof setCalendarUnit>
  | ReturnType<typeof setCurrentDate>
  | ReturnType<typeof setStartedAt>
  | ReturnType<typeof initialize>
  | ReturnType<typeof setSelectedUserUids>
  | ReturnType<typeof initializeForm>
  | ReturnType<typeof setFormType>
  | ReturnType<typeof setDate>
  | ReturnType<typeof removeDate>
  | ReturnType<typeof clearDate>
  | ReturnType<typeof setDuration>
  | ReturnType<typeof setIntervalMinutes>
  | ReturnType<typeof setTimeZone>
  | ReturnType<typeof setPendingEventType>
  | ReturnType<typeof setIsDragging>;

const calculateStartedAt = (
  calendarUnit: CalendarUnitType,
  oldStartedAt: DateTime,
  startWeekDay: number,
) => {
  switch (calendarUnit) {
    case 'month':
      return oldStartedAt.startOf('month');
    case 'week':
      return getWeekStartedAt(oldStartedAt, startWeekDay);
    case '3day':
    case 'day':
      return oldStartedAt.startOf('day');
  }
};

const calculateEndedAt = (
  calendarUnit: CalendarUnitType,
  newStartedAt: DateTime,
) => {
  let durationInput: DurationInput;

  switch (calendarUnit) {
    case 'month':
      durationInput = { months: 1 };
      break;
    case 'week':
      durationInput = { weeks: 1 };
      break;
    case '3day':
      durationInput = { days: 3 };
      break;
    case 'day':
      durationInput = { days: 1 };
      break;
  }

  return newStartedAt.plus(durationInput).minus({ day: 1 }).endOf('day');
};

const reducer = (state: State = initialState(undefined), action: Action) => {
  switch (action.type) {
    case 'SET_CALENDAR_UNIT': {
      const startedAt = getDateTime(state.currentDate, state.timeZone);
      const newStartedAt = calculateStartedAt(
        action.calendarUnit,
        startedAt,
        action.startWeekDay,
      );
      const newEndedAt = calculateEndedAt(action.calendarUnit, newStartedAt);

      return {
        ...state,
        calendarUnit: action.calendarUnit,
        startedAt: newStartedAt.toJSDate(),
        endedAt: newEndedAt.toJSDate(),
      };
    }
    case 'SET_CURRENT_DATE':
      return {
        ...state,
        currentDate: action.currentDate,
      };
    case 'SET_STARTED_AT': {
      const startedAtLuxon = getDateTime(action.startedAt, state.timeZone);
      const newEndedAt = calculateEndedAt(state.calendarUnit, startedAtLuxon);
      return {
        ...state,
        startedAt: startedAtLuxon.toJSDate(),
        endedAt: newEndedAt.toJSDate(),
      };
    }
    case 'INITIALIZE':
      return {
        ...state,
        initialized: true,
        isUsingCache: action.isUsingCache,
        accountCalendars: action.accountCalendars.map((account) => ({
          ...account,
        })),
      };
    case 'SET_SELECTED_USER_UIDS':
      return {
        ...state,
        selectedUserUids: action.selectedUserUids,
      };
    case 'INITIALIZE_FORM':
      return {
        ...state,
        formType: FormType.Event,
        dates: [],
        duration: 30,
        timeZone: action.timeZone || 'Asia/Tokyo',
      };
    case 'SET_FORM_TYPE':
      return {
        ...state,
        formType: action.formType,
      };
    case 'SET_DATE': {
      if (
        state.formType === FormType.Event &&
        state.dates.length > 0 &&
        typeof action.index !== 'number'
      ) {
        return state;
      }

      let dates: EventDate[];
      let targetIndex = action.index;

      // 新規の時、空のdateがあれば、そこを上書きする
      const emptyIndex = state.dates.findIndex(
        (date) => !date.start && !date.end,
      );
      if (targetIndex === undefined && emptyIndex !== -1) {
        targetIndex = emptyIndex;
      }

      // 既存の選択日時とぶつからないか検証し、ぶつかった場合は追加しない
      const isIntersect = state.dates.some((date, index) => {
        if (index === targetIndex) {
          return false;
        }
        return (
          date.start &&
          date.end &&
          action.date.start &&
          action.date.end &&
          ((date.start <= action.date.start && action.date.start < date.end) ||
            (date.start < action.date.end && action.date.end <= date.end) ||
            (action.date.start <= date.start && date.start < action.date.end) ||
            (action.date.start < date.end && date.end <= action.date.end) ||
            (date.start.getTime() === action.date.start.getTime() &&
              date.end.getTime() === action.date.end.getTime()))
        );
      });

      if (isIntersect) {
        dates = state.dates;
      } else if (targetIndex === undefined) {
        dates = [...state.dates, action.date];
      } else {
        dates = [...state.dates];
        dates[targetIndex] = action.date;
      }

      dates = dates.sort((a, b) =>
        a.start && b.start ? (a.start < b.start ? -1 : 1) : 0,
      );

      return {
        ...state,
        dates,
      };
    }
    case 'REMOVE_DATE': {
      switch (state.formType) {
        case FormType.Event:
        case FormType.Schedule:
          const newDates = state.dates.filter((d) => d.id !== action.id);
          return {
            ...state,
            dates: newDates.length === 0 ? [] : [...newDates],
          };
        default:
          break;
      }
      return state;
    }
    case 'CLEAR_DATE': {
      return {
        ...state,
        dates: [],
      };
    }
    case 'SET_DURATION': {
      return {
        ...state,
        duration: action.duration,
      };
    }
    case 'SET_INTERVAL_MINUTES': {
      return {
        ...state,
        intervalMinutes: action.intervalMinutes,
      };
    }
    case 'SET_TIMEZONE': {
      const timeZone = action.timeZone;
      const dates = state.dates.map((date) => {
        return {
          ...date,
          start:
            date.start !== undefined
              ? getDateTime(date.start, state.timeZone)
                  .setZone(timeZone)
                  .toJSDate()
              : undefined,
          end:
            date.end !== undefined
              ? getDateTime(date.end, state.timeZone)
                  .setZone(timeZone)
                  .toJSDate()
              : undefined,
        };
      });
      return {
        ...state,
        timeZone,
        dates,
      };
    }
    case 'SET_PENDING_EVENT_TYPE': {
      return {
        ...state,
        pendingEventType: action.pendingEventType,
      };
    }
    case 'SET_IS_DRAGGING': {
      return {
        ...state,
        isDragging: action.isDragging,
      };
    }
    default:
      return state;
  }
};

const DashboardCalendarContext = React.createContext<{
  state: State;
  dispatch: (action: Action) => void;
}>({
  state: initialState(),
  dispatch: () => {},
});

const DashboardCalendarProvider = ({
  children,
  locale,
  timeZone,
  formType,
  weekStartDay,
}: {
  children: ReactNode;
  locale: string;
  timeZone: string;
  formType?: FormType;
  weekStartDay?: number;
}) => {
  const [state, dispatch] = useReducer(
    reducer,
    initialState(locale, timeZone, formType, weekStartDay),
  );

  return (
    <DashboardCalendarContext.Provider value={{ state, dispatch }}>
      {children}
    </DashboardCalendarContext.Provider>
  );
};

export { DashboardCalendarContext, DashboardCalendarProvider };
